import type {
	PortalSiteSalesContactDto,
	PortalSiteSupportContactDto,
} from '@supply-mover-2-0/shared';

/**
 * PD-17082 — wording for the storefront sign-in pop-up when the visitor may
 * not have an account yet.
 *
 * Asking for a sign-in code answers the same way whether or not an account
 * exists, so the code request cannot be used to find out who has one. That
 * also means the code screen never knows whether a code actually went out,
 * so it says "if ... has an account" instead of promising a code, and offers
 * a way forward to someone who has no account: Register when the brand takes
 * registrations, otherwise the contact details at the foot of the pop-up.
 *
 * These return translation keys. The caller passes them through t().
 */

export type SignInCodeChannel = 'email' | 'sms';

/**
 * The line above the code boxes. It does not repeat the address or number:
 * the identity chip directly above it already shows it. "6\u2011digit" uses a
 * non-breaking hyphen so a phone-width pop-up never wraps it as "6-" / "digit".
 */
export function codeSentMessageKey(channel: SignInCodeChannel): string {
	return channel === 'sms'
		? 'If this account exists, we texted you a 6\u2011digit code.'
		: 'If this account exists, we emailed you a 6\u2011digit code.';
}

/**
 * What to offer someone without an account:
 * - 'register': the brand takes registrations, so show "Register".
 * - 'contact': no registration, but the pop-up's footer lists a phone number
 *   or email address to ask for an account.
 * - 'none': neither, so there is nothing useful to point at.
 */
export type NoAccountPrompt = 'register' | 'contact' | 'none';

/** Shown for 'contact'. */
export const NO_ACCOUNT_CONTACT_KEY =
	"Don't have an account? Contact us using the details below.";

/** The parts of a support or sales contact that someone could reach. */
type ContactLines =
	| Pick<PortalSiteSupportContactDto, 'phone' | 'email'>
	| Pick<PortalSiteSalesContactDto, 'phone' | 'email'>;

/**
 * Whether the footer prints a phone number or email for this contact. Mirrors
 * the footer's own checks: business hours alone give nobody to contact.
 */
function hasContactLine(contact: ContactLines | null): boolean {
	return contact != null && (contact.phone != null || contact.email != null);
}

export function resolveNoAccountPrompt(options: {
	isRegistrationEnabled: boolean;
	supportContact: ContactLines | null;
	salesContact: ContactLines | null;
}): NoAccountPrompt {
	if (options.isRegistrationEnabled) {
		return 'register';
	}
	if (
		hasContactLine(options.supportContact) ||
		hasContactLine(options.salesContact)
	) {
		return 'contact';
	}
	return 'none';
}
